import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { seoArticlesList } from "@/mocks/seoArticlesList";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function ArticleDynamicPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = seoArticlesList.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="w-full max-w-4xl mx-auto px-6 md:px-10 pt-32 pb-20 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <p className="text-foreground-600 mb-8">The article you are looking for does not exist or has been moved.</p>
          <Link
            to="/shopping-guide"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Back to Shopping Guide
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <article className="w-full max-w-4xl mx-auto px-6 md:px-10 pt-32 pb-20">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": article.title,
                "description": article.metaDescription,
                "datePublished": article.datePublished,
                "author": { "@type": "Person", "name": article.author },
                "publisher": { "@type": "Organization", "name": "ItaobuySpreadsheet", "logo": { "@type": "ImageObject", "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg" } },
                "mainEntityOfPage": { "@type": "WebPage", "@id": "https://itaobuy.cfd/article/" + article.slug }
              })
            }}
          />
          {article.faqs && article.faqs.length > 0 && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": article.faqs.map((faq) => ({
                    "@type": "Question",
                    "name": faq.q,
                    "acceptedAnswer": { "@type": "Answer", "text": faq.a }
                  }))
                })
              }}
            />
          )}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://itaobuy.cfd/" },
                  { "@type": "ListItem", "position": 2, "name": "Shopping Guide", "item": "https://itaobuy.cfd/shopping-guide" },
                  { "@type": "ListItem", "position": 3, "name": article.title, "item": "https://itaobuy.cfd/article/" + article.slug }
                ]
              })
            }}
          />
          <nav className="mb-8">
            <Link
              to="/shopping-guide"
              className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Back to Shopping Guide
            </Link>
          </nav>

          <header className="mb-12">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                SEO Article
              </span>
              <span className="text-xs text-foreground-500">{article.datePublished}</span>
              <span className="text-xs text-foreground-500">by {article.author}</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
          </header>

          <div className="mb-12 rounded-2xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-64 md:h-80 object-cover object-top"
            />
          </div>

          <div className="prose-custom">
            {article.sections.map((section, idx) => (
              <section key={idx} className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                {section.content.split("\n\n").map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-foreground-700 leading-relaxed mb-4 text-base">
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}
          </div>

          {article.faqs && article.faqs.length > 0 && (
            <div className="mt-12 p-8 bg-background-100 rounded-2xl border border-background-200/70">
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                Frequently Asked Questions
              </h3>
              <div className="space-y-6">
                {article.faqs.map((faq, idx) => (
                  <div key={idx}>
                    <h4 className="font-heading text-base font-bold text-foreground-950 mb-2">
                      Q{idx + 1}: {faq.q}
                    </h4>
                    <p className="text-foreground-600 text-sm">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-16 p-8 bg-background-100 rounded-2xl border border-background-200/70">
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-3">
              Ready to Start Sourcing?
            </h3>
            <p className="text-foreground-600 text-sm mb-6">
              Visit the main platform to access the full itaobuy spreadsheet and start discovering profitable products today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                itaobuy sheet
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-foreground-950 text-white rounded-lg text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap"
              >
                buy it now
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-foreground-300 text-foreground-700 rounded-lg text-sm font-semibold hover:border-foreground-950 hover:text-foreground-950 transition-colors whitespace-nowrap"
              >
                Back to Homepage
              </Link>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors font-medium"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Return to Homepage — Explore Product Categories
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}